/**
 * The iOS app (the WarCrestOnline Xcode project): the game page runs in its web view, which
 * puts `window.warcrestNative` in every page before our scripts run. Through it the page signs
 * in with Apple or Google, buys gem packs from the App Store (RevenueCat), turns notifications
 * on and plays haptic taps. In a browser there is no bridge and everything here does nothing.
 *
 * App Store rules shape what the app shows: gem packs only through the App Store (never Polar or
 * E-pins), Sign in with Apple beside Google, and the account deletion one tap away.
 */

export type NativeFeature = "apple" | "google" | "iap" | "push" | "haptics";

interface NativeBridge {
  platform: "ios";
  version: string;
  build: string;
  features: readonly string[];
  call(op: string, args?: Record<string, unknown>): Promise<unknown>;
}

declare global {
  interface Window {
    warcrestNative?: NativeBridge;
  }
}

const bridge = (): NativeBridge | undefined =>
  typeof window === "undefined" ? undefined : window.warcrestNative;

/** The page runs in the iOS app. */
export const inApp = (): boolean => !!bridge();

/** The app can do this (an older build may lack it, or its keys may not be set). */
export const nativeHas = (f: NativeFeature): boolean => !!bridge()?.features.includes(f);

/** Asks the app; rejects with the app's short code ("cancelled", "denied", "failed", ...). */
export async function nativeCall<T>(op: string, args?: Record<string, unknown>): Promise<T> {
  const b = bridge();
  if (!b) throw new Error("not_in_app");
  try {
    return (await b.call(op, args)) as T;
  } catch (err) {
    throw new Error(err instanceof Error ? err.message : String(err));
  }
}

/** The short code of a failed call. */
export const nativeError = (err: unknown): string =>
  err instanceof Error ? err.message : String(err);

export interface NativeSignIn {
  idToken: string;
  /** Apple only: the raw nonce the token was asked with, and the code the api trades. */
  nonce?: string;
  code?: string;
}

export const nativeSignIn = (provider: "apple" | "google") =>
  nativeCall<NativeSignIn>("signIn", { provider });

export interface NativeProduct {
  id: string;
  /** The App Store's price in the player's currency and format ("₺179,99", "$4.99"). */
  price: string;
  currency: string;
}

export const nativeProducts = (ids: string[]) => nativeCall<NativeProduct[]>("products", { ids });

/** Buys an App Store product for the account; the gems arrive through the api's webhook. */
export const nativeBuy = (productId: string, accountId: string) =>
  nativeCall<{ status: "purchased" | "pending" | "cancelled" }>("buy", { productId, accountId });

/** A light tap, a heavier one, or the system's success / warning / error pattern. */
export type HapticStyle = "light" | "medium" | "heavy" | "success" | "warning" | "error";

let lastTap = 0;
/** Plays a haptic tap in the app (at most one each 60 ms); nothing in a browser. */
export function haptic(style: HapticStyle = "light") {
  if (!nativeHas("haptics")) return;
  const now = performance.now();
  if (now - lastTap < 60) return;
  lastTap = now;
  void nativeCall("haptic", { style }).catch(() => undefined);
}

/** Opens a page outside the game: an in-app Safari sheet for our site, Safari for the rest. */
export function openOutside(url: string) {
  const abs = new URL(url, location.href).href;
  if (!inApp()) {
    window.open(abs, "_blank", "noopener");
    return;
  }
  void nativeCall("openExternal", { url: abs, inApp: new URL(abs).origin === location.origin });
}

/** The player signed out: the app forgets the store's account. */
export function nativeSignedOut() {
  if (inApp()) void nativeCall("signedOut").catch(() => undefined);
}

/** The account id inside an access token (its `sub`), for the store's purchases. */
export function accountIdOf(accessToken: string): string | null {
  try {
    const part = accessToken.split(".")[1] ?? "";
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    const sub = (JSON.parse(json) as { sub?: unknown }).sub;
    return typeof sub === "string" ? sub : null;
  } catch {
    return null;
  }
}
